// Ledger.tsx — Item 7: the one ADAPTIVE INSPECTOR in the right gutter.
// CONTRACT:C4-DESIGN-TOKENS.1.0
// Spec: Tak-TDF COP.dc.html:287-428 (selection envelope + TDF addendum + the
//        no-selection Picture Summary), and docs/ux/SHELL-COMPOSITION.md §2.5,
//        §4(4) Esc, §4(5) heavy-panel, §6 R-LAW-1 (provenance leads).
//
// Tier-1/2a re-skin to the canon mock:
//   SELECTION  → full-span CLEARTEXT classification banner → ENVELOPE mast (eyebrow,
//                callsign, "i" trust button, ✕ dismiss, affiliation + staleness
//                chips) → rich OP-DETAIL grid (flag · opType · ORIGIN/DOMAIN + a
//                2-col HEADING/SPEED/ALT/POSITION/TRACK/LAST grid) → PROTECTED
//                CONTENT·TDF addendum (grey lock → Trust modal, inner banner,
//                sealed/clear payload) → footer action pair (primary teal · Trust).
//   NO SELECT  → PICTURE SUMMARY 2×2 stat cards + RELEASE MODEL legend (+ Glomar
//                explanation) + a lock-hint footer.
//
// KAS VERDICT is NO LONGER ambient chrome here (rule 4): the terminal grant/deny
// verdict lives ONLY in the Trust modal (openTdf via `onOpenTrust`). The inspector
// surfaces an "Open Trust & provenance →" affordance instead, so the operator is
// never left with no path to the KAS signal.
//
// GLOMAR: renders ONLY over `status.selected` (an already-cleared envelope) and
// counts that are already visible-only. Banners carry CLASSIFICATION ONLY (banner
// purity). React stays OUT of the render loop: this reads the immutable CopStatus
// snapshot and calls imperative CopHandle methods.

import type { CSSProperties } from "react";
import type { CopStatus, CopHandle, PictureStatTone } from "../host/cop.ts";
import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import { bannerColor, bannerTextColor, classificationLabel } from "./dod-marking.ts";
import { PayloadCard } from "./PayloadCard.tsx";
import { affiliationToStatus, type StatusName } from "../../../../packages/design-tokens/src/status.ts";
import { useHeavyPanel } from "./heavyPanel.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./ledger.css";

/** Map a PictureStat semantic tone → the design token that colours its card. */
const STAT_TONE_VAR: Record<PictureStatTone, string> = {
  released: "var(--color-semantic-grant, #46D98E)",
  reduced: "var(--color-semantic-caution, #F2C14E)",
  requestable: "var(--ui-accent, #1FB6B6)",
  hostile: "var(--status-hostile, #FF5256)",
};

/** The RELEASE MODEL legend chips — static design constants (no host seam). */
const RELEASE_LEGEND: { name: string; desc: string; color: string }[] = [
  { name: "Clear", desc: "Full fidelity, payload readable", color: "var(--color-semantic-grant, #46D98E)" },
  { name: "Reduced", desc: "Released at lower detail", color: "var(--color-semantic-caution, #F2C14E)" },
  { name: "Coarsened", desc: "Position only, for deconfliction", color: "var(--status-pending, #FF9E40)" },
  { name: "Requestable", desc: "Advertised — available on request", color: "var(--ui-accent, #1FB6B6)" },
];

/** Affiliation hue (the --status-* token) for the selected entity's mast accent +
 *  chip. Falls back to "unknown" when the affiliation is missing/unrecognised. */
function affiliationHue(env: CanonicalEnvelope): { name: StatusName; cssVar: string } {
  const raw = String(env.properties.affiliation ?? "").toLowerCase();
  const known = ["friend", "hostile", "neutral", "unknown", "pending"];
  const aff = (known.includes(raw) ? raw : "unknown") as
    "friend" | "hostile" | "neutral" | "unknown" | "pending";
  const name = affiliationToStatus(aff);
  return { name, cssVar: `var(--status-${name})` };
}

/** The raw CoT XML for the (already-cleared) selected envelope, as the sibling
 *  gateway lane populates it on `properties.rawCoT`. Returns a non-empty string
 *  only when a real string is present — never fabricates. The embedded
 *  `<__tdf>`/ZTDF inner block will appear here as opaque text; that is EXPECTED
 *  (the inspector does not decode it). This reads ONLY the cleared outer envelope
 *  surfaced past the PDP, so it cannot reveal anything the PDP would withhold. */
function rawCoTOf(env: CanonicalEnvelope): string | null {
  const raw = (env.properties as Record<string, unknown>)?.rawCoT;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed === "" ? null : raw;
}

/** First finite number among the candidates, else undefined. Lets a reader accept
 *  any of the equivalent property spellings the data sources emit (e.g. the theater
 *  loaders publish `heading_deg`/`speed_kn`; ADS-B publishes `heading`/`speed`). */
function num(...candidates: unknown[]): number | undefined {
  for (const c of candidates) {
    const n = typeof c === "number" ? c : typeof c === "string" ? Number(c) : NaN;
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** A small deterministic 0..1 hash of a string — used for stable synthetic-but-coherent
 *  fields (a track number, a grid offset) so the SAME object always reads the same way.
 *  Mirrors the theater loaders' hash01 so derived values are consistent across surfaces. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/** Nation → flag gradient (mirrors the canon mock's FLAG map, COP.dc.html:1019). The
 *  tile is a CSS background string; "—" is the neutral unknown-nation slate. Structural
 *  flag gradients are presentation constants — not design tokens (no token exists for a
 *  national flag); the unknown fallback uses the slate hairline rgba from the mock. */
const FLAG: Record<string, string> = {
  USA: "linear-gradient(180deg,#3C3B6E 0 34%,#fff 34% 67%,#B22234 67%)",
  GBR: "linear-gradient(135deg,#012169 0 50%,#C8102E 50%)",
  PRC: "#DE2910",
  IRN: "linear-gradient(180deg,#239F40 0 33%,#fff 33% 67%,#DA0000 67%)",
  ISR: "linear-gradient(180deg,#0038B8 0 18%,#fff 18% 82%,#0038B8 82%)",
  TWN: "linear-gradient(135deg,#FE0000 60%,#000095 60%)",
  PAN: "linear-gradient(90deg,#005293 0 38%,#fff 38% 62%,#DA121A 62%)",
  AUS: "#00247D",
  NZL: "linear-gradient(135deg,#00247D 58%,#CC142B 58%)",
  CAN: "linear-gradient(90deg,#FF0000 0 28%,#fff 28% 72%,#FF0000 72%)",
  NGO: "var(--ui-accent, #1FB6B6)",
  NSA: "repeating-linear-gradient(45deg,#3a4350 0 4px,#23272e 4px 8px)",
  "—": "rgba(125,145,165,.18)",
};

/** Fallback nation when an object carries no explicit nation/origin — derived from
 *  affiliation + domain so the flag tile reads coherently (friendly ⇒ coalition;
 *  hostile ⇒ the named adversary for the theater; neutral/unknown ⇒ the no-nation
 *  slate). Pure presentation — invents NO security signal (the marking is untouched). */
function fallbackNation(env: CanonicalEnvelope): string {
  const p = env.properties ?? {};
  const aff = String(p.affiliation ?? "").toLowerCase();
  const src = String(p.source ?? env.provenance?.originator ?? "").toLowerCase();
  if (aff === "friend") {
    // Coalition: read the first FVEY code off releasability, else USA.
    const rel = env.security?.releasability ?? [];
    const code = rel.find((r) => FLAG[String(r).toUpperCase()]);
    return code ? String(code).toUpperCase() : "USA";
  }
  if (aff === "hostile") {
    // The named adversary tracks the theater id baked into source/originator.
    if (src.includes("taiwan")) return "PRC";
    if (src.includes("levant")) return "IRN";
    return "IRN"; // Hormuz default theater
  }
  if (aff === "neutral") return "NGO"; // civil / relief / merchant traffic
  return "—"; // unknown / pending — honestly no nation
}

/** Resolve the nation CODE for the flag tile + ORIGIN line: an explicit property first
 *  (p.nation / p.origin / p.origin_country — the live ADS-B + any enriched object), else
 *  the affiliation-derived fallback. Up-cased to match the FLAG map keys. */
function nationCode(env: CanonicalEnvelope): string {
  const p = env.properties ?? {};
  const explicit = p.nation ?? p.origin ?? p.origin_country;
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit).trim().toUpperCase();
  }
  return fallbackNation(env);
}

/** The flag gradient for a nation code (unknown ⇒ slate). */
function flagBackground(code: string): string {
  return FLAG[code] ?? FLAG["—"];
}

/** kind → a human opType label, the fallback when an object carries no `type` platform
 *  string. The data sources mostly DO carry a rich `type` (e.g. "civil-airliner",
 *  "aircraft-carrier"); this only backstops a bare kind. */
const KIND_TYPE_LABEL: Record<string, string> = {
  track: "Track contact",
  alert: "Alert / event",
  observation: "Field observation",
  asset: "Friendly asset",
};

/** Pretty-print a kebab/snake platform type ("civil-airliner" ⇒ "Civil Airliner"). */
function prettyType(raw: string): string {
  return raw
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The opType line: an explicit `type` platform string (prettified) wins; else the
 *  kind→label fallback; else the raw kind. */
function opTypeLabel(env: CanonicalEnvelope): string {
  const p = env.properties ?? {};
  const t = p.type;
  if (typeof t === "string" && t.trim() !== "" && t.toLowerCase() !== env.kind) {
    return prettyType(t);
  }
  return KIND_TYPE_LABEL[env.kind] ?? prettyType(env.kind);
}

/** DOMAIN label for the ORIGIN line, normalised to the mock's vocabulary. */
function domainLabel(env: CanonicalEnvelope): string {
  const p = env.properties ?? {};
  const d = String(p.domain ?? "").toLowerCase();
  switch (d) {
    case "air":
      return "AIR";
    case "maritime":
    case "sea":
    case "surface":
      return "SURFACE";
    case "sub":
    case "subsurface":
      return "SUBSURFACE";
    case "ground":
    case "land":
      return "GROUND";
    case "uas":
    case "uav":
      return "UAS";
    case "cyber":
      return "CYBER";
    default:
      return d ? d.toUpperCase() : env.kind.toUpperCase();
  }
}

/** Derive a coarse staleness label from the envelope's declared stale time. Pure
 *  presentation — does NOT change membership (that is the host's job). */
function staleLabel(env: CanonicalEnvelope): string {
  const stale = env.valid_time?.stale;
  if (typeof stale !== "string") return "LIVE";
  const t = Date.parse(stale);
  if (Number.isNaN(t)) return "LIVE";
  const dtMin = (t - Date.now()) / 60000;
  if (dtMin <= 0) return "LOST";
  if (dtMin < 2) return "AGING";
  return "LIVE";
}

/** A relative-age LAST string from the envelope's declared stale window. The theater
 *  loaders + ADS-B both stamp a real `valid_time.stale`; this turns it into the mock's
 *  "live / N min ago / LOST" readout. Pure presentation (does NOT change membership). */
function lastSeen(env: CanonicalEnvelope): string {
  const label = staleLabel(env);
  if (label === "LOST") return "LOST";
  const start = env.valid_time?.start ?? env.time;
  const t = typeof start === "string" ? Date.parse(start) : NaN;
  if (Number.isNaN(t)) return "live";
  const ageMin = Math.round((Date.now() - t) / 60000);
  if (ageMin <= 0) return "live";
  return `${ageMin} min ago`;
}

/** A synthetic MGRS-style grid from a Point geometry — deterministic per object so the
 *  POSITION reads like a real grid ref (mirrors the mock's "37S DC ####  ####" form)
 *  rather than a bare lon/lat decimal. An explicit `p.mgrs` always wins. */
function positionGrid(env: CanonicalEnvelope): string {
  const p = env.properties ?? {};
  if (typeof p.mgrs === "string" && p.mgrs.trim() !== "") return p.mgrs.trim();
  if (env.geometry?.type === "Point") {
    const c = env.geometry.coordinates as unknown;
    if (Array.isArray(c) && typeof c[0] === "number" && typeof c[1] === "number") {
      const lon = c[0] as number;
      const lat = c[1] as number;
      // GZD from longitude (UTM 6° zones), a stable square pair, and a 4+4 easting/
      // northing pulled from the fractional minutes — a faithful grid-shaped readout.
      const zone = Math.floor((lon + 180) / 6) + 1;
      const band = "CDEFGHJKLMNPQRSTUVWX"[Math.min(19, Math.max(0, Math.floor((lat + 80) / 8)))] ?? "S";
      const sq = `${"ABCDEFGH"[Math.abs(Math.round(lon)) % 8]}${"ABCDEFGH"[Math.abs(Math.round(lat)) % 8]}`;
      const e = String(Math.round((((lon % 1) + 1) % 1) * 9999)).padStart(4, "0");
      const n = String(Math.round((((lat % 1) + 1) % 1) * 9999)).padStart(4, "0");
      return `${zone}${band} ${sq} ${e} ${n}`;
    }
  }
  return "—";
}

/** The six op-detail rows, read from REAL envelope fields (the theater loaders publish
 *  heading_deg/speed_kn; ADS-B publishes heading/speed/altitude) with domain-aware
 *  formatting + "—" fallbacks. We invent NO host seams: a field genuinely absent on
 *  `properties`/geometry renders "—" (or a domain-coherent derived value). */
function opRows(env: CanonicalEnvelope): { k: string; v: string }[] {
  const p = (env.properties ?? {}) as Record<string, unknown>;
  const domain = String(p.domain ?? "").toLowerCase();
  const isAir = domain === "air" || domain === "uas" || domain === "uav";
  const isSub = domain === "sub" || domain === "subsurface";
  const isGround = domain === "ground" || domain === "land";
  const isSea = domain === "maritime" || domain === "sea" || domain === "surface";

  // HEADING: heading_deg (theaters) / heading|true_track (ADS-B). "—" when static.
  const headingDeg = num(p.heading_deg, p.heading, p.true_track, p.headingDeg);
  const heading = headingDeg !== undefined ? `${Math.round(headingDeg)}°T` : "—";

  // SPEED: speed_kn (theaters) / speed|speed_kt (explicit) / velocity_ms→kn (ADS-B).
  let speedKn = num(p.speed_kn, p.speed, p.speed_kt, p.speedKn);
  if (speedKn === undefined) {
    const ms = num(p.velocity_ms, p.velocity);
    if (ms !== undefined) speedKn = ms * 1.94384; // m/s → knots
  }
  const speed =
    speedKn === undefined
      ? "—"
      : speedKn <= 0
        ? isGround
          ? "STATIC"
          : "0 kn"
        : `${Math.round(speedKn)} kn`;

  // ALT / DEPTH / ELEV — label + value flex with the domain.
  const altLabel = isSub ? "DEPTH" : isGround ? "ELEV" : "ALTITUDE";
  // altitude_m (ADS-B, metres) / altitude|altitude_ft|altFt (explicit feet) / depth (metres).
  // The scenario corpus authors altitude inside the motion block (motion.altitude_ft); read it
  // defensively so the value surfaces the moment the theater loader promotes it to properties.
  const motion = (p.motion ?? {}) as Record<string, unknown>;
  const altM = num(p.altitude_m);
  const altFt = num(p.altitude, p.altitude_ft, p.altFt, p.alt_ft, motion.altitude_ft);
  const depthM = num(p.depth, p.depth_m, p.depthM, motion.depth_m);
  let altValue = "—";
  if (isSub) {
    altValue = depthM !== undefined ? `${Math.round(depthM)} m` : "SURFACE";
  } else if (isAir) {
    const ft = altFt ?? (altM !== undefined ? altM * 3.28084 : undefined);
    altValue = ft !== undefined ? `FL${String(Math.round(ft / 100)).padStart(3, "0")}` : "—";
  } else if (isSea) {
    altValue = depthM !== undefined ? `${Math.round(depthM)} m` : "SURFACE";
  } else if (isGround) {
    const ft = altFt ?? (altM !== undefined ? altM * 3.28084 : undefined);
    altValue = ft !== undefined ? `${Math.round(ft * 0.3048)} m` : "110 m";
  } else if (altM !== undefined || altFt !== undefined) {
    const ft = altFt ?? (altM as number) * 3.28084;
    altValue = `FL${String(Math.round(ft / 100)).padStart(3, "0")}`;
  }

  // TRACK: explicit track number, else a deterministic synthetic TN from the id.
  const explicitTrack = num(p.track_number, p.track, p.trackNumber);
  const track =
    explicitTrack !== undefined
      ? `TN ${Math.round(explicitTrack)}`
      : `TN ${7000 + Math.round(hash01(env.id) * 2999)}`;

  return [
    { k: "HEADING", v: heading },
    { k: "SPEED", v: speed },
    { k: altLabel, v: altValue },
    { k: "POSITION", v: positionGrid(env) },
    { k: "TRACK", v: track },
    { k: "LAST", v: lastSeen(env) },
  ];
}

/** "View raw" disclosure for the inspector TDF addendum: a native <details> (no
 *  React state — React stays out of the render loop, like the RELEASE MODEL
 *  legend) that reveals the raw CoT XML of the already-cleared selected envelope.
 *  When the gateway lane has not (yet) populated `rawCoT`, we show an HONEST
 *  "raw CoT unavailable" state — we never fabricate XML. The embedded
 *  `<__tdf>`/ZTDF inner block renders as opaque text by design. Copy-to-clipboard
 *  is offered as a best-effort nicety (guarded; no-ops where unsupported). */
function RawCoTDisclosure({ raw }: { raw: string | null }): JSX.Element {
  const labelStyle: CSSProperties = {
    fontFamily: "var(--typography-family-mono, \"IBM Plex Mono\", ui-monospace, monospace)",
    fontSize: "9px",
    color: "#6c7886",
    letterSpacing: "0.1em",
    cursor: "pointer",
  };
  return (
    <details style={{ marginTop: "10px" }}>
      <summary
        className="ledger__rawcot-summary"
        style={{ ...labelStyle, listStyle: "none", display: "flex", alignItems: "center", gap: "7px" }}
      >
        <span>VIEW RAW · CoT XML</span>
      </summary>
      {raw === null ? (
        <div
          style={{
            marginTop: "8px",
            fontFamily: "var(--typography-family-mono, \"IBM Plex Mono\", ui-monospace, monospace)",
            fontSize: "10px",
            color: "#8a97a6",
            fontStyle: "italic",
            border: "1px dashed rgba(125, 145, 165, 0.26)",
            borderRadius: "var(--radius-control, 7px)",
            padding: "10px 12px",
          }}
        >
          raw CoT unavailable
        </div>
      ) : (
        <div style={{ marginTop: "8px" }}>
          <button
            type="button"
            onClick={() => {
              // Best-effort copy; silently degrade where the clipboard API is absent
              // (e.g. non-secure context). Never throws into the render path.
              try {
                void navigator?.clipboard?.writeText?.(raw);
              } catch {
                /* clipboard unavailable — the <pre> below is still selectable */
              }
            }}
            style={{
              background: "color-mix(in srgb, var(--ui-accent) 12%, transparent)",
              border: "1px solid color-mix(in srgb, var(--ui-accent) 36%, transparent)",
              color: "#9fe9e9",
              borderRadius: "6px",
              fontFamily: "var(--typography-family-hud, \"Saira\", system-ui, sans-serif)",
              fontWeight: 600,
              fontSize: "10px",
              padding: "3px 9px",
              cursor: "pointer",
              marginBottom: "6px",
            }}
          >
            Copy
          </button>
          <pre
            style={{
              margin: 0,
              maxHeight: "220px",
              overflow: "auto",
              fontFamily: "var(--typography-family-mono, \"IBM Plex Mono\", ui-monospace, monospace)",
              fontSize: "10px",
              lineHeight: 1.5,
              color: "#cfd8e2",
              background: "rgba(10, 16, 24, 0.55)",
              border: "1px solid rgba(125, 145, 165, 0.16)",
              borderRadius: "var(--radius-control, 7px)",
              padding: "10px 12px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {raw}
          </pre>
        </div>
      )}
    </details>
  );
}

export function Ledger({
  status,
  handle,
  onOpenTrust,
}: {
  status: CopStatus;
  handle: CopHandle;
  /** Open the ONE shared Trust & provenance modal (rule 5). Wired from BOTH the
   *  masthead "i" button AND the grey lock in the PROTECTED CONTENT addendum. */
  onOpenTrust: () => void;
}): JSX.Element {
  const sel = status.selected;

  // The GraphView (relationship graph) is a HEAVY summon owned by the Ledger
  // (R-LAW-3). NOTE: the graph-summon ("Relationships") action MOVES OUT of the
  // inspector footer in this tier → it relocates to a Throughline DO command in a
  // later tier. We keep the heavy claim registered (so Esc still pops a live graph
  // before clearing the selection) but no longer render a footer button for it.
  const graph = useHeavyPanel("graph");

  // Esc pops ONE level: drop a live relationship graph first; else clear selection.
  useEscapeStack(() => {
    if (graph.isActive) graph.release();
    else handle.clearSelection();
  }, sel !== null);

  // ---- REST STATE: the PICTURE SUMMARY (right-gutter calm face) ----
  if (!sel) {
    const { stats } = status.pictureSummary;
    const requestable = status.pictureSummary.requestable;
    return (
      <section className="ledger ledger--rest" aria-label="Ledger · picture summary">
        <div className="ledger__rest-head">
          <div className="ledger__rest-eyebrow">PICTURE SUMMARY</div>
          <div className="ledger__rest-tagline">{status.pictureSummary.headline}</div>
        </div>

        {/* 2×2 stat cards: RELEASED / REDUCED / REQUESTABLE / HOSTILE·KNOWN */}
        <div className="ledger__stats">
          {stats.map((s) => (
            <div
              className="ledger__stat"
              key={s.tone}
              style={{ ["--stat-color" as string]: STAT_TONE_VAR[s.tone] }}
            >
              <div className="ledger__stat-value">{s.value}</div>
              <div className="ledger__stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* REQUESTABLE teaser — only when a count exists (forward-looking; 0 today). */}
        {requestable > 0 && (
          <button type="button" className="ledger__request-teaser">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0" />
            </svg>
            <div className="ledger__request-teaser-body">
              <div className="ledger__request-teaser-title">
                {requestable} dataset{requestable > 1 ? "s" : ""} may be available
              </div>
              <div className="ledger__request-teaser-sub">Request access to unlock</div>
            </div>
            <span className="ledger__request-teaser-arrow">→</span>
          </button>
        )}

        {/* RELEASE MODEL legend + the Glomar explanation (a <details> disclosure). */}
        <div className="ledger__legend">
          <details className="ledger__legend-disclose">
            <summary className="ledger__legend-head">
              <span className="ledger__legend-title">RELEASE MODEL</span>
              <span className="ledger__legend-i" aria-hidden="true">i</span>
            </summary>
            <div className="ledger__legend-glomar">
              You see only what is released to you.{" "}
              <span className="ledger__legend-glomar-hi">
                Data you aren&rsquo;t cleared to know about isn&rsquo;t dimmed or withheld — it simply
                isn&rsquo;t here.
              </span>{" "}
              Switch persona, or set a lower ceiling, to re-filter the picture.
            </div>
          </details>
          {RELEASE_LEGEND.map((lp) => (
            <div className="ledger__legend-row" key={lp.name}>
              <span
                className="ledger__legend-chip"
                style={{ ["--chip-color" as string]: lp.color }}
                aria-hidden="true"
              />
              <div className="ledger__legend-text">
                <div className="ledger__legend-name">{lp.name}</div>
                <div className="ledger__legend-desc">{lp.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* bottom lock hint — the actionable framing for the whole picture */}
        <div className="ledger__lockhint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0" />
          </svg>
          <span>
            Click any <span className="ledger__lockhint-hi">lock</span> to inspect the cryptographic
            trust &amp; provenance behind it.
          </span>
        </div>
      </section>
    );
  }

  // ---- SELECTED: re-skin to the entity (CLEARTEXT banner → mast → op-detail → TDF) ----
  const p = (sel.properties ?? {}) as Record<string, unknown>;
  const isProtected = p.tdf_protected === true || sel.security.payload_mode === "encrypted";
  const granted = status.decrypt.state === "granted";
  // The protected (inner) classification: the decrypted marking when the KAS
  // released it; else the outer floor (a positive "you are cleared" tell).
  const innerClass = granted && status.decrypt.marking
    ? status.decrypt.marking.classification
    : sel.security.classification;
  const name = String(sel.properties.name ?? sel.id);
  const hue = affiliationHue(sel);
  const outerClass = sel.security.classification;
  // The lock SVG path toggles open (key released) vs closed (sealed).
  const lockPath = granted ? "M8 11V8a4 4 0 0 1 7-2.6" : "M8 11V8a4 4 0 0 1 8 0v3";
  // Footer primary action label flexes with the payload state.
  const primaryLabel = granted ? "Track" : isProtected ? "Request access" : "Track";

  return (
    <section className="ledger ledger--entity" aria-label="Ledger · inspect">
      {/* ── full-span OUTER (cleartext) classification banner — CLASSIFICATION ONLY ── */}
      <div
        className="ledger__banner"
        style={{ background: bannerColor(outerClass), color: bannerTextColor(outerClass) }}
      >
        <span className="ledger__banner-text">{classificationLabel(outerClass)}</span>
      </div>

      {/* ── ENVELOPE mast: eyebrow · callsign · "i" trust · ✕ · aff/stale chips ── */}
      <header
        className="ledger__mast"
        style={{
          borderTop: `2px solid ${hue.cssVar}`,
          background: `linear-gradient(180deg, color-mix(in srgb, ${hue.cssVar} 8%, transparent), transparent)`,
        }}
      >
        <div className="ledger__mast-top">
          <div className="ledger__mast-id">
            <div className="ledger__mast-eyebrow">ENVELOPE · {sel.kind.toUpperCase()}</div>
            <div className="ledger__mast-call">{name}</div>
          </div>
          <div className="ledger__mast-actions">
            <button
              type="button"
              className="ledger__info-btn"
              onClick={onOpenTrust}
              title="Trust &amp; provenance"
            >
              i
            </button>
            <button
              type="button"
              className="ledger__dismiss-btn"
              onClick={() => handle.clearSelection()}
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="ledger__mast-chips">
          <span
            className="ledger__aff-chip"
            style={{ ["--chip-hue" as string]: hue.cssVar }}
          >
            {hue.name.toUpperCase()}
          </span>
          <span className="ledger__stale-chip">{staleLabel(sel)}</span>
        </div>
      </header>

      <div className="ledger__scroll">
        {/* ── RICH OPERATIONAL DETAIL ── */}
        <div className="ledger__op">
          <div className="ledger__op-head">
            <div
              className="ledger__op-flag"
              style={{ background: flagBackground(nationCode(sel)) }}
              title={`Nation · ${nationCode(sel)}`}
              aria-hidden="true"
            />
            <div className="ledger__op-id">
              <div className="ledger__op-type">{opTypeLabel(sel)}</div>
              <div className="ledger__op-origin">
                ORIGIN {nationCode(sel)} · {domainLabel(sel)}
              </div>
            </div>
          </div>
          <div className="ledger__op-grid">
            {opRows(sel).map((o) => (
              <div className="ledger__op-cell" key={o.k}>
                <div className="ledger__op-k">{o.k}</div>
                <div className="ledger__op-v">{o.v}</div>
              </div>
            ))}
          </div>
          {typeof p.note === "string" && p.note.trim() !== "" && (
            <div className="ledger__op-note">{p.note}</div>
          )}
        </div>

        {/* ── PROTECTED CONTENT · TDF addendum (only for protected payloads) ── */}
        {isProtected && (
          <div className="ledger__tdf">
            <div className="ledger__tdf-head">
              <span className="ledger__tdf-label">PROTECTED CONTENT · TDF</span>
              <span className="ledger__tdf-rule" aria-hidden="true" />
            </div>
            <div className="ledger__tdf-row">
              <button
                type="button"
                className="ledger__lock-btn"
                onClick={onOpenTrust}
                title="Trust &amp; provenance"
                aria-label="Open trust and provenance"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="11" width="14" height="9" rx="2" />
                  <path d={lockPath} />
                </svg>
              </button>
              <div
                className="ledger__inner-banner"
                style={{ background: bannerColor(innerClass), color: bannerTextColor(innerClass) }}
              >
                {classificationLabel(innerClass)}
              </div>
            </div>
            <div className="ledger__tdf-body">
              {granted ? (
                // CLEAR payload — released to you; render the RICH payload card
                // (full CAPCO header + structured/prose body + view-raw, see
                // PayloadCard.tsx). A grant with NO sealed detail (a live track)
                // keeps the honest "[ TRACK STATE · LIVE ]" thumb.
                status.decrypt.plaintext ? (
                  <PayloadCard
                    plaintext={status.decrypt.plaintext}
                    marking={status.decrypt.marking}
                  />
                ) : (
                  <div className="ledger__payload-thumb">
                    <span>[ TRACK STATE · LIVE ]</span>
                  </div>
                )
              ) : (
                // SEALED — key not held; offer a (forward-looking) request affordance.
                <div className="ledger__payload-locked">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="5" y="11" width="14" height="9" rx="2" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                  </svg>
                  <div className="ledger__payload-locked-text">
                    Protected content · {classificationLabel(innerClass)} · open Trust to see the key verdict
                  </div>
                  <button type="button" className="ledger__request-btn" onClick={onOpenTrust}>
                    Open Trust &amp; provenance →
                  </button>
                </div>
              )}
              {/* "view raw" — the raw CoT XML of the already-cleared OUTER envelope.
                  In-scope because the inspector only opens over a VISIBLE (cleared)
                  entity (`status.selected`); the embedded `<__tdf>` block stays
                  opaque (we don't decode it here). */}
              <RawCoTDisclosure raw={rawCoTOf(sel)} />
            </div>
          </div>
        )}
      </div>

      {/* ── footer action pair: primary (teal) + Trust (grey, opens modal) ──
           The standalone "◇ Relationships" graph-summon button is DROPPED from the
           footer in this tier; it relocates to a Throughline DO command in a later
           tier. The primary action flexes with the payload state: a SEALED protected
           payload routes "Request access" into the Trust modal (where the KAS verdict
           lives, rule 4); an open/cleared entity summons its relationship graph
           ("Track" context) — keeping the heavy-panel claim (R-LAW-3) exercised. */}
      <footer className="ledger__footer">
        <button
          type="button"
          className="ledger__action-primary"
          onClick={() => {
            if (isProtected && !granted) {
              onOpenTrust();
              return;
            }
            graph.claim();
            handle.openGraph(sel.id);
          }}
        >
          {primaryLabel}
        </button>
        <button type="button" className="ledger__action-trust" onClick={onOpenTrust}>
          Trust
        </button>
      </footer>
    </section>
  );
}
